import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useDataContext } from "context";
import { QueryKeys, client, queryClient } from "queryClient";
import { Expansion, StockTask } from "types";
import {
  getStockTaskList,
  getStockTaskListByExpansion,
} from "modules/stocktasks/api";

type StockTaskListContext = {
  stockTaskList: StockTask[];
  loadStockTaskList: (
    nextTokenParam?: string | null | undefined,
    loadAll?: boolean,
  ) => Promise<StockTask[]>;
  hasNextData: boolean;
  nextToken: string | null;
  isLoadingStockTaskList: boolean;
  unsyncedExpansionList: Expansion[];
  loadUnsyncedExpansionList: () => Promise<Expansion[]>;
  isLoadingUnsyncedExpansionList: boolean;
};

type StockTaskListParams = {
  expansionID: string;
};

const StockTaskListUIContext = createContext<StockTaskListContext>(
  {} as StockTaskListContext,
);

export function useStockTaskListUIContext() {
  return useContext(StockTaskListUIContext);
}

export const StockTaskListUIConsumer = StockTaskListUIContext.Consumer;

type StockTaskListUIProviderProps = {
  children: React.ReactNode;
};

const StockTaskListUIProvider: React.FC<StockTaskListUIProviderProps> = ({
  children,
}) => {
  const dataContext = useDataContext();

  const { expansionID } = useParams<StockTaskListParams>();

  const [isLoadingStockTaskList, setIsLoadingStockTaskList] =
    useState<boolean>(true);

  const [stockTaskList, setStockTaskList] = useState<StockTask[]>([]);
  const [hasNextData, setHasNextData] = useState<boolean>(true);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const [isLoadingUnsyncedExpansionList, setIsLoadingUnsyncedExpansionList] =
    useState<boolean>(false);

  const [unsyncedExpansionList, setUnsyncedExpansionList] = useState<
    Expansion[]
  >([]);

  useEffect(() => {
    // Subscribe to creation of StockTask
    const createSub = client.models.StockTask.onCreate(
      expansionID
        ? {
            filter: {
              expansionID: {
                eq: expansionID,
              },
            },
          }
        : undefined,
    ).subscribe({
      next: (data) => {
        console.log("Create StockTask", data);
        setStockTaskList((prev) => [data, ...prev]);
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to update of StockTask
    const updateSub = client.models.StockTask.onUpdate(
      expansionID
        ? {
            filter: {
              expansionID: {
                eq: expansionID,
              },
            },
          }
        : undefined,
    ).subscribe({
      next: (data) => {
        console.log("Update StockTask", data);
        if (data.status === "Completed") {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.Expansions],
          });
          loadUnsyncedExpansionList();
        }
        setStockTaskList((prev) =>
          prev.map((item) => (item.id === data.id ? data : item)),
        );
      },
      error: (error) => console.warn(error),
    });

    // Subscribe to deletion of StockTask
    const deleteSub = client.models.StockTask.onDelete(
      expansionID
        ? {
            filter: {
              expansionID: {
                eq: expansionID,
              },
            },
          }
        : undefined,
    ).subscribe({
      next: (data) => {
        console.log("Delete StockTask", data);
        setStockTaskList((prev) => prev.filter((item) => item.id !== data.id));
      },
      error: (error) => console.warn(error),
    });

    return () => {
      createSub.unsubscribe();
      updateSub.unsubscribe();
      deleteSub.unsubscribe();
    };
  }, []);

  const loadStockTaskList = useCallback(
    async (
      nextTokenParam?: string | null | undefined,
      loadAll?: boolean,
    ): Promise<StockTask[]> => {
      setIsLoadingStockTaskList(true);
      const timestamp1 = new Date();

      const { newStockTaskList, newNextToken } = expansionID
        ? await getStockTaskListByExpansion(
            expansionID,
            nextTokenParam,
            loadAll,
          )
        : await getStockTaskList(nextTokenParam, loadAll);

      console.log("newStockTaskList", newStockTaskList);

      if (nextTokenParam) {
        setStockTaskList((prev) => [...prev, ...newStockTaskList]);
      } else {
        setStockTaskList(newStockTaskList);
      }

      setHasNextData(newStockTaskList.length === 50);
      setNextToken(newNextToken);

      const timestamp2 = new Date();
      const timeDiff = timestamp2.getTime() - timestamp1.getTime();

      console.log(Math.round(timeDiff) + " ms for loading");
      setIsLoadingStockTaskList(false);

      return newStockTaskList;
    },
    [expansionID],
  );

  useEffect(() => {
    if (dataContext.isLoadingStateEntities) {
      return;
    }
    loadStockTaskList();
  }, [loadStockTaskList, dataContext.isLoadingStateEntities]);

  const loadUnsyncedExpansionList = async () => {
    setIsLoadingUnsyncedExpansionList(true);

    const { data } = await client.queries.listUnsyncedExpansions();

    setUnsyncedExpansionList(data ?? []);

    setIsLoadingUnsyncedExpansionList(false);

    return data ?? [];
  };

  useEffect(() => {
    loadUnsyncedExpansionList();
  }, []);

  const value = {
    stockTaskList,
    setStockTaskList,
    loadStockTaskList,
    isLoadingStockTaskList,
    hasNextData,
    nextToken,
    unsyncedExpansionList,
    loadUnsyncedExpansionList,
    isLoadingUnsyncedExpansionList,
  };

  return (
    <StockTaskListUIContext.Provider value={value}>
      {children}
    </StockTaskListUIContext.Provider>
  );
};

export default StockTaskListUIProvider;
